// Phase 05 instrument breadth (docs/05 §5.4). Synth-based instruments are
// instant (no assets); the grand piano streams Salamander samples from a CDN
// on demand. Shared helpers keep each definition small.

import * as Tone from "tone";
import { degreeToNote } from "../util/scales.ts";
import type { NoteEvent, ScaleName } from "../util/types.ts";
import {
  clamp01,
  clampVelocity,
  macroToCutoff,
  type Instrument,
  type InstrumentCategory,
  type PlayArchetype,
} from "./types.ts";

interface Meta {
  id: string;
  name: string;
  category: InstrumentCategory;
  archetype: PlayArchetype;
  polyphony: "mono" | "poly" | number;
}

interface VoicePoly {
  triggerAttackRelease(note: Tone.Unit.Frequency, dur: Tone.Unit.Time, time?: Tone.Unit.Time, vel?: number): void;
  triggerAttack(note: Tone.Unit.Frequency, time?: Tone.Unit.Time, vel?: number): void;
  triggerRelease(note: Tone.Unit.Frequency, time?: Tone.Unit.Time): void;
  connect(dst: Tone.InputNode): void;
  dispose(): void;
  volume: Tone.Param<"decibels">;
}

interface VoiceMono {
  triggerAttackRelease(note: Tone.Unit.Frequency, dur: Tone.Unit.Time, time?: Tone.Unit.Time, vel?: number): void;
  triggerAttack(note: Tone.Unit.Frequency, time?: Tone.Unit.Time, vel?: number): void;
  triggerRelease(time?: Tone.Unit.Time): void;
  connect(dst: Tone.InputNode): void;
  dispose(): void;
  volume: Tone.Param<"decibels">;
}

type MacroKind = "filter" | "swell" | "none";

function macroToSwell(v: number): number {
  return 0.15 + 0.85 * clamp01(v);
}

function makePoly(
  meta: Meta,
  build: () => VoicePoly,
  cfg: { baseOctave: number; dur: Tone.Unit.Time; macro: MacroKind; defaultMacro: number; reverb?: boolean },
): () => Instrument {
  return () => {
    let synth: VoicePoly | null = null;
    let filter: Tone.Filter | null = null;
    let swell: Tone.Gain | null = null;
    let verb: Tone.Freeverb | null = null;
    let key = "C";
    let scale: ScaleName = "majorPentatonic";
    const resolve = (ev: NoteEvent): Tone.Unit.Frequency =>
      ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, cfg.baseOctave) : ((ev.note ?? "C4") as Tone.Unit.Frequency);

    return {
      ...meta,
      scaleLocked: true,
      init(output) {
        let node: Tone.ToneAudioNode = output;
        if (cfg.macro === "filter") {
          filter = new Tone.Filter({ type: "lowpass", frequency: macroToCutoff(cfg.defaultMacro), Q: 0.6 });
          filter.connect(node);
          node = filter;
        } else if (cfg.macro === "swell") {
          swell = new Tone.Gain(macroToSwell(cfg.defaultMacro));
          swell.connect(node);
          node = swell;
        }
        if (cfg.reverb) {
          verb = new Tone.Freeverb({ roomSize: 0.8, dampening: 2200, wet: 0.3 });
          verb.connect(node);
          node = verb;
        }
        synth = build();
        synth.connect(node);
      },
      trigger(ev, time) {
        synth?.triggerAttackRelease(resolve(ev), ev.duration ?? cfg.dur, time, clampVelocity(ev.velocity));
      },
      attack(ev, time) {
        synth?.triggerAttack(resolve(ev), time, clampVelocity(ev.velocity));
      },
      release(ev, time) {
        synth?.triggerRelease(resolve(ev), time);
      },
      setScale(k, s) {
        key = k;
        scale = s;
      },
      setMacro(v) {
        if (filter) filter.frequency.rampTo(macroToCutoff(v), 0.05);
        else if (swell) swell.gain.rampTo(macroToSwell(v), 0.1);
      },
      dispose() {
        synth?.dispose();
        filter?.dispose();
        swell?.dispose();
        verb?.dispose();
        synth = null;
        filter = null;
        swell = null;
        verb = null;
      },
    };
  };
}

function makeMono(
  meta: Meta,
  build: () => VoiceMono,
  cfg: { baseOctave: number; dur: Tone.Unit.Time; defaultMacro: number },
): () => Instrument {
  return () => {
    let synth: VoiceMono | null = null;
    let filter: Tone.Filter | null = null;
    let key = "C";
    let scale: ScaleName = "majorPentatonic";
    const resolve = (ev: NoteEvent): Tone.Unit.Frequency =>
      ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, cfg.baseOctave) : ((ev.note ?? "C3") as Tone.Unit.Frequency);

    return {
      ...meta,
      scaleLocked: true,
      init(output) {
        filter = new Tone.Filter({ type: "lowpass", frequency: macroToCutoff(cfg.defaultMacro), Q: 1 });
        filter.connect(output);
        synth = build();
        synth.connect(filter);
      },
      trigger(ev, time) {
        synth?.triggerAttackRelease(resolve(ev), ev.duration ?? cfg.dur, time, clampVelocity(ev.velocity));
      },
      attack(ev, time) {
        synth?.triggerAttack(resolve(ev), time, clampVelocity(ev.velocity));
      },
      release(_ev, time) {
        synth?.triggerRelease(time);
      },
      setScale(k, s) {
        key = k;
        scale = s;
      },
      setMacro(v) {
        filter?.frequency.rampTo(macroToCutoff(v), 0.05);
      },
      dispose() {
        synth?.dispose();
        filter?.dispose();
        synth = null;
        filter = null;
      },
    };
  };
}

function makePluck(meta: Meta, baseOctave: number): () => Instrument {
  return () => {
    // PluckSynth is monophonic and quiet, so a single instance cuts every
    // strum/chord down to one note. Use a round-robin voice pool into a makeup
    // gain so chords ring out and the level matches the other instruments.
    const VOICES = 6;
    let pool: Tone.PluckSynth[] = [];
    let gain: Tone.Gain | null = null;
    let rr = 0;
    let key = "C";
    let scale: ScaleName = "majorPentatonic";
    const resolve = (ev: NoteEvent): Tone.Unit.Frequency =>
      ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, baseOctave) : ((ev.note ?? "C4") as Tone.Unit.Frequency);
    return {
      ...meta,
      scaleLocked: true,
      init(output) {
        gain = new Tone.Gain(1.8).connect(output); // PluckSynth runs quiet; make it up
        for (let i = 0; i < VOICES; i++) {
          pool.push(new Tone.PluckSynth({ attackNoise: 1, dampening: 4000, resonance: 0.95 }).connect(gain));
        }
      },
      trigger(ev, time) {
        const voice = pool[rr % pool.length];
        rr++;
        voice?.triggerAttack(resolve(ev), time);
      },
      setScale(k, s) {
        key = k;
        scale = s;
      },
      dispose() {
        for (const v of pool) v.dispose();
        gain?.dispose();
        pool = [];
        gain = null;
      },
    };
  };
}

// --- Explicit factories --------------------------------------------------

export const createOrgan = makePoly(
  { id: "organ", name: "Organ", category: "keys", archetype: "air-keys", polyphony: "poly" },
  () => new Tone.PolySynth(Tone.Synth, { oscillator: { type: "square" }, envelope: { attack: 0.02, decay: 0.05, sustain: 0.9, release: 0.3 } }),
  { baseOctave: 4, dur: "8n", macro: "filter", defaultMacro: 0.7 },
);

export const createMarimba = makePoly(
  { id: "marimba", name: "Marimba", category: "keys", archetype: "air-keys", polyphony: "poly" },
  () => new Tone.PolySynth(Tone.FMSynth, { harmonicity: 8, modulationIndex: 2, envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.3 }, modulationEnvelope: { attack: 0.002, decay: 0.2, sustain: 0 } }),
  { baseOctave: 4, dur: "8n", macro: "filter", defaultMacro: 0.85 },
);

export const createBells = makePoly(
  { id: "bells", name: "Bells", category: "perc", archetype: "air-keys", polyphony: "poly" },
  () => new Tone.PolySynth(Tone.FMSynth, { harmonicity: 3.01, modulationIndex: 14, envelope: { attack: 0.001, decay: 1.4, sustain: 0, release: 1.4 }, modulationEnvelope: { attack: 0.001, decay: 0.5, sustain: 0 } }),
  { baseOctave: 5, dur: "4n", macro: "filter", defaultMacro: 0.95 },
);

export const createStrings = makePoly(
  { id: "strings", name: "String ensemble", category: "strings", archetype: "theremin", polyphony: "poly" },
  () => new Tone.PolySynth(Tone.Synth, { oscillator: { type: "sawtooth" }, envelope: { attack: 0.4, decay: 0.3, sustain: 0.9, release: 1.6 } }),
  { baseOctave: 3, dur: "2n", macro: "swell", defaultMacro: 0.7, reverb: true },
);

export const createSynthBrass = makePoly(
  { id: "synth-brass", name: "Synth brass", category: "wind", archetype: "air-keys", polyphony: "poly" },
  () => new Tone.PolySynth(Tone.Synth, { oscillator: { type: "sawtooth" }, envelope: { attack: 0.04, decay: 0.2, sustain: 0.7, release: 0.3 } }),
  { baseOctave: 4, dur: "8n", macro: "filter", defaultMacro: 0.8 },
);

export const createFmBass = makeMono(
  { id: "fm-bass", name: "FM bass", category: "bass", archetype: "theremin", polyphony: "mono" },
  () => new Tone.FMSynth({ harmonicity: 1, modulationIndex: 6, oscillator: { type: "sine" }, envelope: { attack: 0.01, decay: 0.2, sustain: 0.6, release: 0.3 } }),
  { baseOctave: 2, dur: "8n", defaultMacro: 0.6 },
);

export const createSynthLead = makeMono(
  { id: "synth-lead", name: "Synth lead", category: "leads", archetype: "theremin", polyphony: "mono" },
  () => new Tone.MonoSynth({ oscillator: { type: "sawtooth" }, filter: { Q: 2 }, envelope: { attack: 0.02, decay: 0.2, sustain: 0.7, release: 0.4 } }),
  { baseOctave: 4, dur: "8n", defaultMacro: 0.75 },
);

export const createKalimba = makePluck(
  { id: "kalimba", name: "Kalimba", category: "plucked", archetype: "air-keys", polyphony: "poly" },
  4,
);

export const createHarp = makePluck(
  { id: "harp", name: "Harp", category: "plucked", archetype: "strum", polyphony: "poly" },
  4,
);

export function createGrandPiano(): Instrument {
  let sampler: Tone.Sampler | null = null;
  let fallback: Tone.PolySynth<Tone.Synth> | null = null; // sounds until samples load
  let key = "C";
  let scale: ScaleName = "majorPentatonic";
  const resolve = (ev: NoteEvent): Tone.Unit.Frequency =>
    ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, 4) : ((ev.note ?? "C4") as Tone.Unit.Frequency);
  return {
    id: "grand-piano",
    name: "Grand piano",
    category: "keys",
    archetype: "air-keys",
    scaleLocked: true,
    polyphony: "poly",
    init(output) {
      // A plain synth covers the gap so the piano is never silent: the
      // Salamander samples stream from a CDN on demand and can take a second
      // (or fail offline). We play the fallback until `sampler.loaded`.
      fallback = new Tone.PolySynth(Tone.Synth);
      fallback.set({ oscillator: { type: "triangle" }, envelope: { attack: 0.004, decay: 0.7, sustain: 0.08, release: 0.9 } });
      fallback.connect(output);
      sampler = new Tone.Sampler({
        urls: { A2: "A2.mp3", A3: "A3.mp3", A4: "A4.mp3", A5: "A5.mp3" },
        baseUrl: "https://tonejs.github.io/audio/salamander/",
        release: 1,
      });
      sampler.connect(output);
    },
    trigger(ev, time) {
      const note = resolve(ev);
      const dur = ev.duration ?? "4n";
      const v = clampVelocity(ev.velocity);
      if (sampler?.loaded) sampler.triggerAttackRelease(note, dur, time, v);
      else fallback?.triggerAttackRelease(note, dur, time, v);
    },
    setScale(k, s) {
      key = k;
      scale = s;
    },
    dispose() {
      sampler?.dispose();
      fallback?.dispose();
      sampler = null;
      fallback = null;
    },
  };
}

export function createElectroKit(): Instrument {
  let bus: Tone.Gain | null = null;
  let kick: Tone.MembraneSynth | null = null;
  let snare: Tone.NoiseSynth | null = null;
  let hat: Tone.NoiseSynth | null = null;
  let hatHP: Tone.Filter | null = null;
  let clap: Tone.NoiseSynth | null = null;
  let tom: Tone.MembraneSynth | null = null;
  let crash: Tone.NoiseSynth | null = null;
  let crashHP: Tone.Filter | null = null;
  return {
    id: "electro-kit",
    name: "808 kit",
    category: "drums",
    archetype: "air-drum",
    scaleLocked: false,
    polyphony: 6,
    init(output) {
      bus = new Tone.Gain().connect(output);
      kick = new Tone.MembraneSynth({ pitchDecay: 0.08, octaves: 8, envelope: { attack: 0.001, decay: 0.6, sustain: 0, release: 0.1 } }).connect(bus);
      tom = new Tone.MembraneSynth({ pitchDecay: 0.03, octaves: 4, envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.1 } }).connect(bus);
      snare = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 0.14, sustain: 0 } }).connect(bus);
      snare.volume.value = -8;
      clap = new Tone.NoiseSynth({ noise: { type: "pink" }, envelope: { attack: 0.002, decay: 0.12, sustain: 0 } }).connect(bus);
      clap.volume.value = -10;
      hatHP = new Tone.Filter({ type: "highpass", frequency: 9000 }).connect(bus);
      hat = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 0.035, sustain: 0 } }).connect(hatHP);
      hat.volume.value = -6;
      crashHP = new Tone.Filter({ type: "highpass", frequency: 5000 }).connect(bus);
      crash = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 1.0, sustain: 0, release: 0.4 } }).connect(crashHP);
      crash.volume.value = -12;
    },
    trigger(ev, time) {
      const v = clampVelocity(ev.velocity);
      const when = time ?? Tone.now();
      switch (ev.note) {
        case "kick": kick?.triggerAttackRelease("C1", "8n", when, v); break;
        case "tom": tom?.triggerAttackRelease("G2", "8n", when, v); break;
        case "snare": snare?.triggerAttackRelease("8n", when, v); break;
        case "clap": clap?.triggerAttackRelease("8n", when, v); break;
        case "hat": hat?.triggerAttackRelease("16n", when, v); break;
        case "crash": crash?.triggerAttackRelease("1n", when, v); break;
      }
    },
    dispose() {
      for (const n of [kick, tom, snare, clap, hat, hatHP, crash, crashHP, bus]) n?.dispose();
      bus = kick = snare = clap = tom = null;
      hat = crash = null;
      hatHP = crashHP = null;
    },
  };
}

export function createShaker(): Instrument {
  let noise: Tone.NoiseSynth | null = null;
  let filter: Tone.Filter | null = null;
  return {
    id: "shaker",
    name: "Shaker",
    category: "perc",
    archetype: "shake",
    scaleLocked: false,
    polyphony: 1,
    init(output) {
      filter = new Tone.Filter({ type: "highpass", frequency: 6000 });
      filter.connect(output);
      noise = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 0.05, sustain: 0 } });
      noise.volume.value = -10;
      noise.connect(filter);
    },
    trigger(ev, time) {
      noise?.triggerAttackRelease("16n", time ?? Tone.now(), clampVelocity(ev.velocity));
    },
    dispose() {
      noise?.dispose();
      filter?.dispose();
      noise = null;
      filter = null;
    },
  };
}
